/**
 * Names of published sites: `https://<name>.tabbio.site`
 * (docs/planning/agent-artifacts-phase2-plan-2026-09-24.md, section 9.6).
 *
 * Shared by the API (availability, publish, rename), the sites service and the
 * clients. The static rules (format, reserved and blocked names) are checked
 * here with no I/O. The rules that need the database (another site has the
 * name, a hold, a member's username) are decided by
 * `resolveSiteNameAvailability` from facts the API looks up, so every reason
 * has one precedence everywhere.
 *
 * Imported by the API, the mobile app and the native node:test runner, so its
 * runtime dependencies use explicit package exports (see
 * parsed-cv-to-overrides.ts), never an extensionless relative import.
 */

import type {
  PublishedSiteStatus,
  SiteNameReservationReason,
  SiteNameUnavailableReason,
} from "@tabbio/types";
import { RESERVED_PUBLIC_SUBDOMAINS } from "@tabbio/utils/public-url";
import {
  RESERVED_USERNAMES,
  SUBSTRING_BLOCKLIST_EXTRAS,
  TARGETED_SUBSTRING_KEYWORDS,
  USERNAME_ALLOWLIST,
} from "@tabbio/utils/username-policy";

export const SITE_NAME_MIN_LENGTH = 3;
export const SITE_NAME_MAX_LENGTH = 40;

/**
 * A DNS label of 3 to 40 lowercase letters, digits and hyphens that starts and
 * ends with a letter or digit. The same pattern is the SQL CHECK
 * `published_site_name_format`, which also refuses "--".
 */
export const SITE_NAME_PATTERN = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

/** Longest slug taken from a title for a suggestion. */
export const SITE_NAME_SLUG_MAX_LENGTH = 30;

/**
 * Infrastructure, account and trust words a site may never be called, on top
 * of `RESERVED_PUBLIC_SUBDOMAINS` and `RESERVED_USERNAMES`.
 */
export const SITE_RESERVED_NAMES = [
  "www",
  "api",
  "app",
  "admin",
  "mail",
  "email",
  "smtp",
  "imap",
  "pop",
  "ftp",
  "ns",
  "ns1",
  "ns2",
  "dns",
  "cdn",
  "static",
  "assets",
  "media",
  "img",
  "images",
  "files",
  "fonts",
  "status",
  "health",
  "report",
  "abuse",
  "security",
  "legal",
  "terms",
  "privacy",
  "help",
  "support",
  "docs",
  "blog",
  "about",
  "login",
  "logout",
  "signin",
  "signup",
  "register",
  "auth",
  "oauth",
  "sso",
  "account",
  "accounts",
  "billing",
  "pay",
  "payment",
  "payments",
  "checkout",
  "wallet",
  "bank",
  "secure",
  "verify",
  "verification",
  "update",
  "recovery",
  "password",
  "reset",
  "root",
  "localhost",
  "test",
  "example",
  "internal",
  "origin",
  "preview",
  "sites",
  "site",
] as const;

/**
 * Credential and payment words that mark a phishing name anywhere in it. They
 * are also matched with the hyphens removed ("sign-in", "seed-phrase").
 */
export const SITE_BLOCKED_SUBSTRINGS = [
  "login",
  "signin",
  "verify",
  "password",
  "wallet",
  "seedphrase",
  "paypal",
  "appleid",
] as const;

const RESERVED = new Set<string>([
  ...RESERVED_PUBLIC_SUBDOMAINS,
  ...RESERVED_USERNAMES,
  ...SITE_RESERVED_NAMES,
]);

/** The username policy's substring blocklist (profanity and the Tabbio brand). */
const USERNAME_BLOCKED_SUBSTRINGS: readonly string[] = [
  ...new Set<string>([
    ...TARGETED_SUBSTRING_KEYWORDS,
    ...SUBSTRING_BLOCKLIST_EXTRAS,
  ]),
];

/** Also matched with the hyphens removed, so "tab-bio" and "pay-pal" are caught. */
const COMPACT_BLOCKED_SUBSTRINGS: readonly string[] = [
  ...new Set<string>([
    ...SITE_BLOCKED_SUBSTRINGS,
    ...SUBSTRING_BLOCKLIST_EXTRAS,
  ]),
];

/** Why a name breaks the format rules (all of them are reason `invalid`). */
export type SiteNameFormatIssue =
  | "empty"
  | "non_ascii"
  | "too_short"
  | "too_long"
  | "charset"
  | "edge_hyphen"
  | "double_hyphen"
  | "all_digits";

/** NFKC, trimmed and lowercased: the form a name is checked and stored in. */
export function normalizeSiteName(input: string): string {
  return (input ?? "").normalize("NFKC").trim().toLowerCase();
}

/** The first format rule a normalized name breaks, or null when it is well formed. */
export function siteNameFormatIssue(name: string): SiteNameFormatIssue | null {
  if (name.length === 0) return "empty";
  // ASCII only: no IDN, so no look-alike Unicode.
  if (/[^\x00-\x7f]/.test(name)) return "non_ascii";
  if (name.length < SITE_NAME_MIN_LENGTH) return "too_short";
  if (name.length > SITE_NAME_MAX_LENGTH) return "too_long";
  if (/[^a-z0-9-]/.test(name)) return "charset";
  if (name.startsWith("-") || name.endsWith("-")) return "edge_hyphen";
  // Also refuses "xn--" punycode labels.
  if (name.includes("--")) return "double_hyphen";
  if (/^[0-9]+$/.test(name)) return "all_digits";
  return SITE_NAME_PATTERN.test(name) ? null : "charset";
}

/** A reserved platform, brand or infrastructure name (exact match). */
export function isReservedSiteName(name: string): boolean {
  return RESERVED.has(name);
}

/**
 * The blocked word a name contains, or null. Words of the username blocklist
 * are matched in the name as written, skipping hyphen-separated words that its
 * allowlist names ("classic-cars" passes although "classic" contains a
 * blocked word). Credential words and the Tabbio brand are also matched with
 * the hyphens removed.
 */
export function blockedSiteNameTerm(name: string): string | null {
  const masked = name
    .split("-")
    .map((word) => (USERNAME_ALLOWLIST.has(word) ? "" : word))
    .join("-");
  for (const term of USERNAME_BLOCKED_SUBSTRINGS) {
    if (masked.includes(term)) return term;
  }
  const compact = name.replace(/-/g, "");
  for (const term of COMPACT_BLOCKED_SUBSTRINGS) {
    if (compact.includes(term)) return term;
  }
  return null;
}

/** Reasons the static rules alone can give. */
export type SiteNamePolicyReason = Extract<
  SiteNameUnavailableReason,
  "invalid" | "reserved" | "blocked"
>;

export type SiteNamePolicyResult =
  | { ok: true; normalized: string }
  | {
      ok: false;
      normalized: string;
      reason: SiteNamePolicyReason;
      /** Set for `invalid`. */
      issue?: SiteNameFormatIssue;
      /** Set for `blocked`: the word that matched. */
      term?: string;
    };

/**
 * The static policy, in order: format (`invalid`), the reserved lists
 * (`reserved`), then blocked words (`blocked`).
 */
export function checkSiteNamePolicy(input: string): SiteNamePolicyResult {
  const normalized = normalizeSiteName(input);
  const issue = siteNameFormatIssue(normalized);
  if (issue) return { ok: false, normalized, reason: "invalid", issue };
  if (isReservedSiteName(normalized)) {
    return { ok: false, normalized, reason: "reserved" };
  }
  const term = blockedSiteNameTerm(normalized);
  if (term) return { ok: false, normalized, reason: "blocked", term };
  return { ok: true, normalized };
}

/** Who wants the name: the caller, and the artifact when publishing or renaming. */
export interface SiteNameClaimant {
  userId: string;
  artifactId?: string | null;
}

/**
 * What the API found in the database for one normalized name. Absent or null
 * means nothing was found.
 */
export interface SiteNameFacts {
  /**
   * The `published_site` row whose current name this is. A draft has no name
   * (`name` is NULL until the first publish), so it never holds one; a draft
   * passed here is ignored.
   */
  site?: {
    userId: string;
    artifactId: string;
    status: PublishedSiteStatus;
  } | null;
  /** A site renamed away from this name, while the old name still redirects. */
  previousNameOf?: {
    userId: string;
    artifactId: string;
    until: Date;
  } | null;
  /** The `site_name_reservation` row for this name. */
  reservation?: {
    reason: SiteNameReservationReason;
    /** The holder for reason `held` (null for a deleted account's name). */
    userId: string | null;
    /** Null means permanent. */
    expiresAt: Date | null;
  } | null;
  /**
   * The name equals a Tabbio username or company slug. `callerMayUse` is true
   * for the member who owns the username and for the company's members.
   */
  handle?: { callerMayUse: boolean } | null;
}

export type SiteNameResolution =
  | { available: true; normalized: string }
  | {
      available: false;
      normalized: string;
      reason: SiteNameUnavailableReason;
      issue?: SiteNameFormatIssue;
      term?: string;
    };

/**
 * Whether `claimant` may use the name, with one precedence for every caller:
 * `invalid`, `reserved`, `blocked` (the static policy, then an admin or abuse
 * ban, or a suspended site), `username_protected`, `taken` (another site,
 * including another of the caller's own), then `held` (an owner's hold after
 * an unpublish, a rename's old name, a deleted account's name, or an admin's
 * `allow` reservation, which keeps the name for its holder). The owner of a
 * hold, of an unpublished site or of a renamed site may take the name back;
 * the artifact that already has the name keeps it. A draft site holds no
 * name, so it never makes one unavailable.
 */
export function resolveSiteNameAvailability(
  input: string,
  claimant: SiteNameClaimant,
  facts: SiteNameFacts = {},
  now: Date = new Date(),
): SiteNameResolution {
  const policy = checkSiteNamePolicy(input);
  const normalized = policy.normalized;
  if (!policy.ok) {
    const { reason, issue, term } = policy;
    return {
      available: false,
      normalized,
      reason,
      ...(issue ? { issue } : {}),
      ...(term ? { term } : {}),
    };
  }
  const unavailable = (
    reason: SiteNameUnavailableReason,
  ): SiteNameResolution => ({
    available: false,
    normalized,
    reason,
  });

  const reservation =
    facts.reservation &&
    (facts.reservation.expiresAt === null ||
      facts.reservation.expiresAt.getTime() > now.getTime())
      ? facts.reservation
      : null;
  // A draft has not claimed an address yet (its name is NULL).
  const site = facts.site && facts.site.status !== "draft" ? facts.site : null;
  const renamed =
    facts.previousNameOf && facts.previousNameOf.until.getTime() > now.getTime()
      ? facts.previousNameOf
      : null;
  const isOwn = (owner: { userId: string | null }) =>
    owner.userId !== null && owner.userId === claimant.userId;
  const isSameArtifact = (owner: { artifactId: string }) =>
    Boolean(claimant.artifactId) && owner.artifactId === claimant.artifactId;

  if (
    reservation &&
    (reservation.reason === "admin" || reservation.reason === "abuse")
  ) {
    return unavailable("blocked");
  }
  if (site?.status === "suspended") return unavailable("blocked");

  if (facts.handle && !facts.handle.callerMayUse) {
    return unavailable("username_protected");
  }

  if (site && !isSameArtifact(site)) {
    if (site.status === "live") return unavailable("taken");
    // Unpublished (or any other state that is not live): held for its owner.
    if (!isOwn(site)) return unavailable("held");
  }
  if (renamed && !isOwn(renamed)) return unavailable("held");
  if (
    (reservation?.reason === "held" || reservation?.reason === "allow") &&
    !isOwn(reservation)
  ) {
    return unavailable("held");
  }

  return { available: true, normalized };
}

/**
 * A title as a site name: ASCII letters and digits joined by single hyphens,
 * accents and apostrophes dropped ("Sara's Café" is "saras-cafe"), cut to
 * `maxLength` on a clean edge. Empty when the title has no Latin letters or
 * digits (an Arabic title, for example).
 */
export function slugifySiteName(
  text: string,
  maxLength: number = SITE_NAME_SLUG_MAX_LENGTH,
): string {
  return (text ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['\u2018\u2019\u02bc]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, maxLength)
    .replace(/-+$/g, "");
}

const SUFFIX_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";

/** Three random letters or digits. */
export function randomSiteNameSuffix(
  random: () => number = Math.random,
): string {
  let suffix = "";
  for (let i = 0; i < 3; i += 1) {
    const index = Math.min(
      SUFFIX_ALPHABET.length - 1,
      Math.floor(random() * SUFFIX_ALPHABET.length),
    );
    suffix += SUFFIX_ALPHABET[index];
  }
  return suffix;
}

export interface SiteNameSuggestionInput {
  /** The artifact's title. */
  title: string;
  /** The caller's username, when they have one. */
  username?: string | null;
  /** Injected for tests; defaults to Math.random. */
  random?: () => number;
  /** Random-suffix candidates to prepare (default 5). */
  randomCandidates?: number;
}

/** Used as the base when neither the title nor the username gives a usable slug. */
const FALLBACK_SITE_NAME_BASE = "my-site";

/** Room left for "-" and the three-character suffix. */
const RANDOM_BASE_MAX_LENGTH = SITE_NAME_MAX_LENGTH - 4;

function cutToLabel(name: string, maxLength: number = SITE_NAME_MAX_LENGTH) {
  return name.slice(0, maxLength).replace(/-+$/g, "");
}

/**
 * Candidate names in the order they are offered: the title's slug, then
 * `<username>-<slug>` (unless the slug already starts with the username),
 * then `<slug>-<3 random letters or digits>`. When the title gives no slug,
 * the username itself comes first (its owner may use it) and is the base of
 * the random names; with neither, "my-site" is. A slug that contains a
 * blocked word is never the base. Every candidate already passes the static
 * policy; the API still checks each one against the database before
 * returning it.
 */
export function siteNameSuggestionCandidates(
  input: SiteNameSuggestionInput,
): string[] {
  const random = input.random ?? Math.random;
  const username = slugifySiteName(input.username ?? "", SITE_NAME_MAX_LENGTH);
  const slug = slugifySiteName(input.title);
  const candidates: string[] = [];
  if (slug) {
    candidates.push(slug);
    if (username && slug !== username && !slug.startsWith(`${username}-`)) {
      candidates.push(cutToLabel(`${username}-${slug}`));
    }
  } else if (username) {
    candidates.push(username);
  }
  const base =
    (slug && blockedSiteNameTerm(slug) === null ? slug : "") ||
    (username && blockedSiteNameTerm(username) === null ? username : "") ||
    FALLBACK_SITE_NAME_BASE;
  if (candidates.length === 0) candidates.push(base);
  const count = Math.max(0, input.randomCandidates ?? 5);
  const stem = cutToLabel(base, RANDOM_BASE_MAX_LENGTH);
  for (let i = 0; i < count; i += 1) {
    candidates.push(`${stem}-${randomSiteNameSuffix(random)}`);
  }
  const seen = new Set<string>();
  return candidates.filter((name) => {
    if (seen.has(name) || !checkSiteNamePolicy(name).ok) return false;
    seen.add(name);
    return true;
  });
}

/**
 * Up to `limit` (default 3) suggestions, each confirmed with `isAvailable`
 * (the API's database check) before it is returned, in candidate order.
 * `exclude` skips names already shown, such as the one the user typed.
 */
export async function suggestSiteNames(
  input: SiteNameSuggestionInput & {
    isAvailable: (name: string) => Promise<boolean> | boolean;
    limit?: number;
    exclude?: readonly string[];
  },
): Promise<string[]> {
  const limit = Math.max(0, input.limit ?? 3);
  const exclude = new Set((input.exclude ?? []).map(normalizeSiteName));
  const suggestions: string[] = [];
  for (const name of siteNameSuggestionCandidates(input)) {
    if (suggestions.length >= limit) break;
    if (exclude.has(name)) continue;
    if (await input.isAvailable(name)) suggestions.push(name);
  }
  return suggestions;
}
